import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, EVENTS, CLASSES, apiError } from "@/lib/reg";

const COMBOS = [["800m", "long_jump"], ["100m", "long_jump"], ["100m", "800m"]];

const toIsoDate = (s) => {
  const m = (s || "").replace(/\s/g, "").match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return /^\d{4}-\d{2}-\d{2}$/.test((s || "").trim()) ? s.trim() : "";
};

// Accepts lines: Name, Father, DOB(DD-MM-YYYY), Mobile — separated by tab, comma or |
export const parseLines = (text) =>
  text.split("\n").map((l) => l.replace(/\*/g, "").replace(/^\s*\d{1,3}[.)]\s+/, "").trim()).filter(Boolean)
    .map((l) => l.split(/\t|\||,/).map((c) => c.trim()).filter(Boolean))
    .map((c) => (/^\d{1,3}\.?$/.test(c[0] || "") && c.length >= 5 ? c.slice(1) : c))
    .filter((c) => c.length >= 4 && /\d{10}/.test(c[3].replace(/\D/g, "")))
    .map((c) => ({ full_name: c[0], father_name: c[1], dob: toIsoDate(c[2]), mobile: c[3].replace(/\D/g, "").slice(-10) }));

export const BulkUpload = ({ open, auth, onClose, onDone }) => {
  const [text, setText] = useState("");
  const [cls, setCls] = useState("9th");
  const [school, setSchool] = useState("Govt Sr Sec School Manpur");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const parsed = parseLines(text);
  const toggle = (id) => setEvents((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const close = () => { setResult(null); setText(""); onClose(); };

  const submit = async () => {
    if (!parsed.length) return toast.error("कोई सही लाइन नहीं मिली / No valid rows found");
    if (school.trim().length < 2) return toast.error("स्कूल का नाम डालें / Enter school");
    setBusy(true);
    try {
      const rows = parsed.map((p, i) => ({
        ...p, parent_mobile: p.mobile, emergency_contact: p.mobile, player_class: cls, school,
        events: events.length ? events : COMBOS[i % 3],
      }));
      const { data } = await axios.post(`${API}/registrations/bulk`, { rows }, auth);
      setResult(data);
      toast.success(`${data.added} खिलाड़ी जोड़े गए / players added`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white max-w-2xl" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-hi text-navy">एक साथ खिलाड़ी जोड़ें · Bulk Add Players</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="bulk-result">
            <p className="font-hi font-bold text-green-700">{result.added} / {result.results.length} जोड़े गए</p>
            <div className="max-h-72 overflow-auto space-y-1 text-sm">
              {result.results.map((r) => (
                <div key={r.row} className={`flex gap-2 p-2 rounded-lg ${r.ok ? "bg-green-50" : "bg-red-50"}`}>
                  {r.ok ? <CheckCircle2 size={16} className="text-green-600 shrink-0" /> : <XCircle size={16} className="text-red-600 shrink-0" />}
                  <span><b>{r.row}. {r.full_name}</b> — {r.ok ? r.registration_id : r.error}</span>
                </div>
              ))}
            </div>
            <button onClick={close} className="btn-saffron w-full justify-center" data-testid="bulk-close-btn">ठीक है · Done</button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 font-hi">
              हर लाइन में: <b>नाम, पिता का नाम, जन्मतिथि (DD-MM-YYYY), मोबाइल</b>। Excel/WhatsApp से कॉपी-पेस्ट कर सकते हैं।
            </p>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} data-testid="bulk-textarea"
              placeholder={"Dev, Deepak, 26-02-2012, 8053919227\nBhushan, Dhanpal, 07-06-2012, 8930344634"}
              className="gp-input !min-h-[160px] py-3 font-mono text-sm" />
            <p className="text-xs text-slate-500" data-testid="bulk-parsed-count">{parsed.length} सही लाइनें मिलीं · valid rows</p>
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <select value={cls} onChange={(e) => setCls(e.target.value)} className="gp-input" data-testid="bulk-class">
                {CLASSES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input value={school} onChange={(e) => setSchool(e.target.value)} className="gp-input" placeholder="School" data-testid="bulk-school" />
            </div>
            <div>
              <p className="text-sm font-semibold text-navy mb-2 font-hi">खेल (कोई न चुनें तो बारी-बारी से 2-2 खेल)</p>
              <div className="grid grid-cols-2 gap-2">
                {EVENTS.map((ev) => (
                  <button type="button" key={ev.id} onClick={() => toggle(ev.id)} className={`ev-chip ${events.includes(ev.id) ? "on" : ""}`} data-testid={`bulk-event-${ev.id}`}>
                    <span className="font-hi font-semibold text-sm">{ev.hi}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500">अभिभावक और आपातकालीन नंबर = खिलाड़ी का मोबाइल नंबर</p>
            <button onClick={submit} disabled={busy} className="btn-saffron w-full justify-center" data-testid="bulk-submit-btn">
              {busy ? <Loader2 className="animate-spin" size={18} /> : `${parsed.length} खिलाड़ी जोड़ें · Add`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
